// C10 헬 보스 각인 「지옥불 각인」(헬 난이도 고유 규칙, PRD 2.1 헬 = 같은 보스 + 각인 하나).
// 헬 판(plan.diff = hell)의 보스방에서 보스가 싸우는 동안 일정 간격으로 영웅 발밑에 각인(원형 경고)을 새긴다.
// 경고가 끝나면 터지고(맞으면 최대 체력 비율 피해) 그 자리에 지옥불 장판이 남는다(안에 서 있으면 주기 화상).
// 장르 상위 ARPG의 고난도 접사(디아블로 '불타는 땅'·로아 '각인 장판')와 같은 역할: 패턴을 외운 보스도 헬에서는 계속 움직이게 만든다.
//
// 공정성(bossRage.ts 원칙과 같다):
//  - 경고 시간(windup)은 회피 쿨다운(0.6초)보다 충분히 길고(1.1초), 보스 경고 판정 순간과 겹치지 않게 위협 예약표(threats)에 같이 예약한다.
//  - 피해는 영웅 최대 체력 비율(장비·불굴 받는 피해 배율은 그대로 적용) — 헬 피해 배율 ×3.5에 또 곱하지 않아 한 방 즉사가 없다.
//  - 보스 등장·쓰러짐·경직 중과 클리어 연출 대기에는 새기지 않는다. 장판은 최대 MAX개(오래된 것부터 덮어쓴다).
// 순수 규칙 + 고정 배열(프레임 루프 안에서 새 객체를 만들지 않는다). 렌더·소리는 view(HellBrandView)와 Ev.HellBrand만 읽는다.
import { Ev, TeleSource } from '../shared/events';
import { BossState, Scene, TeleShape } from '../shared/types';
import { telegraphHit } from './combat';
import type { GameSim } from './game';

/** 각인 수치(세계 틱 60 = 1초) */
export const HELL_BRAND = {
  /** 보스방 입장(전투 시작) 뒤 첫 각인까지 */
  firstDelay: 240,
  /** 각인 사이 간격. 광폭(체력 25% 이하)이면 frenzyPeriod */
  period: 420,
  frenzyPeriod: 300,
  frenzyAt: 0.25,
  /** 경고 길이(1.1초) */
  windup: 66,
  /** 각인 반경 */
  radius: 2.2,
  /** 폭발 피해 = 영웅 최대 체력 × burst */
  burst: 0.18,
  /** 장판: 반경, 지속(4초), 화상 주기(0.5초), 화상 1회 = 최대 체력 × tick */
  pool: { radius: 1.9, ticks: 240, every: 30, tick: 0.03 },
  /** 동시에 남는 장판 수 */
  max: 4,
} as const;

/** Ev.HellBrand a 값 */
export const HellBrandPhase = { Mark: 0, Burst: 1 } as const;

export class HellPool {
  active = false; x = 0; z = 0; radius = 0; ttl = 0; total = 1; clock = 0;
}

/** 렌더·UI 읽기 전용 */
export class HellBrandView {
  /** 이 판이 헬 보스 판인지(각인이 돌 수 있는 판) */
  active = false;
  readonly pools: HellPool[] = Array.from({ length: HELL_BRAND.max }, () => new HellPool());
}

export class HellBrand {
  readonly view = new HellBrandView();
  private clock = 0;
  private next = -1;
  private slot = -1;
  private resolveAt = 0;
  private markX = 0;
  private markZ = 0;
  private planRef: unknown = null;
  constructor(private readonly g: GameSim) {}

  /** 판·방이 바뀔 때(game.ts reset·loadMap) */
  reset(): void {
    this.g.releaseTelegraph(this.slot);
    this.slot = -1; this.next = -1; this.clock = 0;
    for (const p of this.view.pools) p.active = false;
    const plan = this.g.flowController.plan;
    this.view.active = plan.diff === 'hell' && plan.bossHp > 0;
  }

  /** 보스가 싸우는 중인가(등장·경직·쓰러짐 제외) */
  private bossFighting(): boolean {
    const b = this.g.boss;
    return b.hp > 0 && b.state !== BossState.Inactive && b.state !== BossState.Intro && b.state !== BossState.Stagger
      && b.state !== BossState.Dying && b.state !== BossState.Dead;
  }

  /** 세계 틱마다(game.ts stepWorld, 보스 두뇌 다음) */
  step(worldTick: boolean): void {
    // 판 계획이 바뀌면(새 판·재도전) 처음부터: 헬 보스 판인지 다시 정한다
    if (this.planRef !== this.g.flowController.plan) { this.planRef = this.g.flowController.plan; this.reset(); }
    const v = this.view;
    if (!v.active || !worldTick) return;
    const g = this.g;
    if (g.flow.scene !== Scene.Dungeon || g.flow.clearHold > 0 || g.hero.hp <= 0) return;
    this.clock++;
    this.stepPools();
    if (g.flow.scene !== Scene.Dungeon) return;
    const fighting = this.bossFighting();
    if (this.slot >= 0) {
      const t = g.telegraphs[this.slot];
      t.tick = Math.min(t.total, t.total - (this.resolveAt - this.clock) + g.worldFrac);
      if (this.clock < this.resolveAt) return;
      this.burst();
      return;
    }
    if (!fighting) { this.next = -1; return; }
    if (this.next < 0) { this.next = this.clock + HELL_BRAND.firstDelay; return; }
    if (this.clock >= this.next) this.mark();
  }

  private mark(): void {
    const g = this.g, h = g.hero, B = HELL_BRAND;
    const s = g.allocateTelegraph(TeleSource.HellBrand, 0);
    if (s < 0) { this.next = this.clock + 30; return; }
    // 보스 경고와 판정 순간이 겹치지 않게(위협 예약표가 있는 지도만): 안 되면 반 초 뒤 다시
    if (g.map.threatClock && !g.threats.reserve(s, g.threats.now + B.windup)) { g.releaseTelegraph(s); this.next = this.clock + 30; return; }
    this.slot = s; this.resolveAt = this.clock + B.windup;
    this.markX = h.x; this.markZ = h.z;
    const t = g.telegraphs[s];
    t.shape = TeleShape.Circle; t.x = h.x; t.z = h.z; t.radius = B.radius;
    t.angle = t.width = t.tick = 0; t.total = B.windup;
    const frenzy = g.boss.maxHp > 0 && g.boss.hp / g.boss.maxHp <= B.frenzyAt;
    this.next = this.clock + (frenzy ? B.frenzyPeriod : B.period);
    const e = g.emit(Ev.HellBrand); e.x = h.x; e.z = h.z; e.a = HellBrandPhase.Mark; e.b = B.radius; e.c = B.windup; e.d = frenzy ? 1 : 0;
  }

  private burst(): void {
    const g = this.g, h = g.hero, B = HELL_BRAND;
    const t = g.telegraphs[this.slot];
    const hit = telegraphHit(t, h.x, h.z);
    g.fireTelegraph(this.slot); this.slot = -1;
    const hurt = hit && g.combat.hurtHero(Math.round(h.maxHp * B.burst), this.markX, this.markZ, true, 2);
    this.spawnPool(this.markX, this.markZ);
    const e = g.emit(Ev.HellBrand); e.x = this.markX; e.z = this.markZ; e.a = HellBrandPhase.Burst; e.b = B.radius; e.c = hurt ? 1 : 0; e.d = 0;
  }

  private spawnPool(x: number, z: number): void {
    const P = HELL_BRAND.pool;
    let pick: HellPool | null = null;
    for (const p of this.view.pools) if (!p.active) { pick = p; break; }
    if (!pick) { pick = this.view.pools[0]; for (const p of this.view.pools) if (p.ttl < pick.ttl) pick = p; }
    pick.active = true; pick.x = x; pick.z = z; pick.radius = P.radius; pick.ttl = pick.total = P.ticks; pick.clock = P.every;
  }

  private stepPools(): void {
    const g = this.g, h = g.hero, P = HELL_BRAND.pool;
    for (const p of this.view.pools) {
      if (!p.active) continue;
      if (--p.ttl <= 0) { p.active = false; continue; }
      if (--p.clock > 0) continue;
      p.clock = P.every;
      if (h.hp > 0 && Math.hypot(h.x - p.x, h.z - p.z) < p.radius) {
        g.combat.burnHero(Math.max(1, Math.round(h.maxHp * P.tick)), 0);
        if (g.flow.scene !== Scene.Dungeon) return;
      }
    }
  }
}
